/**
 * exportVendorPDF.js
 * ใช้ใน VendorRegisterDetailView
 *
 * ติดตั้ง: npm install jspdf html2canvas --legacy-peer-deps
 *
 * วิธีใช้:
 *   import { ExportVendorPDFButton } from "./exportVendorPDF";
 *   <ExportVendorPDFButton form={form} files={files} />
 */

import React, { useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Button, CircularProgress } from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
async function toBase64(url) {
  const res  = await fetch(url);
  const blob = await res.blob();
  
  // [FIX] เช็ค content-type ก่อน — ถ้าไม่ใช่ image ให้ return ""
  if (!blob.type.startsWith("image/")) {
    console.warn("ไม่ใช่ไฟล์รูป:", blob.type);
    return "";
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

// ── helper: render HTML → jsPDF ─────────────────────────────────────────
async function renderToPDF(htmlContent) {
  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: 794px;
    background: white;
    font-family: 'Sarabun', 'THSarabunNew', Arial, sans-serif;
    font-size: 13px;
    color: #000;
    padding: 0;
  `;
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  const pdf      = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const margin   = 15;
  const contentW = 210 - margin * 2;

  const pages = container.querySelectorAll(".pdf-page");
  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");
    const imgH    = (canvas.height * contentW) / canvas.width;
    if (i > 0) pdf.addPage();
    pdf.addImage(imgData, "PNG", margin, margin, contentW, imgH);
  }

  document.body.removeChild(container);
  return pdf;
}

// ── helpers ─────────────────────────────────────────────────────────────
const cb = (checked) =>
  `<span style="display:inline-block;width:13px;height:13px;border:1.5px solid #000;
   text-align:center;line-height:13px;font-size:11px;margin-right:4px;vertical-align:middle;">
   ${checked ? "✓" : ""}</span>`;

const fullLine = (label, value) =>
  `<div style="margin-bottom:8px;">
     <span style="font-weight:600;">${label}</span>
     <span style="display:inline-block;width:calc(100% - 220px);border-bottom:1px solid #000;
       padding-left:4px;vertical-align:bottom;">${value ?? ""}</span>
   </div>`;

const inlineField = (label, value, minW = "100px") =>
  `<b>${label}</b>
   <span style="display:inline-block;min-width:${minW};border-bottom:1px solid #000;
     padding-left:4px;vertical-align:bottom;margin-right:16px;">${value ?? ""}</span>`;

const sectionTitle = (title) =>
  `<div style="font-weight:700;font-size:13px;margin:14px 0 8px;">${title}</div>`;

const docItem = (checked, label) =>
  `<div style="margin-bottom:5px;">${cb(checked)} ${label}</div>`;

// ── build HTML ───────────────────────────────────────────────────────────
function buildVendorHTML(form, files) {

  const companyFiles  = files?.COMPANY_PROFILE || [];
  const agreementFiles = files?.AGREEMENT      || [];

  // เงื่อนไขการชำระเงิน
  const isTransfer = form.paymentType === "transfer";
  const isCheque   = form.paymentType === "cheque";

  // เครดิตเทอม
  const creditTerm = String(form.creditTerm ?? "");
  const isNoCredit  = creditTerm === "0"  || creditTerm === "";
  const isCredit7   = creditTerm === "7";
  const isCredit15  = creditTerm === "15";
  const isCredit30  = creditTerm === "30";

  const page = `
<div class="pdf-page" style="padding:24px 32px;min-height:1050px;">

  <!-- Header -->
  <table style="width:100%;margin-bottom:14px;">
    <tr>
      <td style="width:22%;vertical-align:middle;">
        <div style="font-size:22px;font-weight:900;letter-spacing:2px;color:#003087;">SINO</div>
        <div style="font-size:9px;color:#555;letter-spacing:1px;">LOGISTICS CORPORATION</div>
      </td>
      <td style="vertical-align:top;font-size:10px;line-height:1.7;">
        <b>SINO Logistics Corporation PLC</b><br/>
        1011 Supalai Grand Tower 7th Floor Unit 05, Rama 3 Road,<br/>
        Chongnonsee, Yannawa, Bangkok, 10120 Thailand<br/>
        บริษัท ไซโน โลจิสติกส์คอร์ปอเรชั่น จำกัด (มหาชน)<br/>
        FM-AC-014 Rev.01, Effective Date: 01-01-2026
      </td>
    </tr>
  </table>

  <!-- Title -->
  <div style="text-align:center;font-weight:700;font-size:16px;margin:4px 0 14px;">
    แบบฟอร์มการเปิดบัญชีผู้ขาย/ผู้ให้บริการรายใหม่ (New Vendor)
  </div>

  <!-- Date / Code -->
  <table style="width:100%;margin-bottom:14px;">
    <tr>
      <td style="width:50%;">${inlineField("วันที่ :", form.date, "180px")}</td>
      <td>${inlineField("รหัสผู้ขาย/ผู้ให้บริการ :", form.vendorCode, "140px")}</td>
    </tr>
  </table>

  <!-- ข้อมูลสำหรับผู้ขาย -->
  <div style="font-weight:700;font-size:14px;margin-bottom:8px;border-bottom:1px solid #000;padding-bottom:3px;">
    ข้อมูลสำหรับผู้ขาย/ผู้ให้บริการ
  </div>

  ${fullLine("ชื่อผู้ขายหรือผู้ให้บริการ (ภาษาไทย)", form.nameTH)}
  ${fullLine("ชื่อผู้ขายหรือผู้ให้บริการ (ภาษาอังกฤษ)", form.nameEN)}
  ${fullLine("สถานที่ตั้ง", form.address1)}
  ${form.address2 ? fullLine("", form.address2) : ""}
  ${fullLine("เลขประจำตัวผู้เสียภาษี", form.taxId)}
  ${fullLine("ประเภทธุรกิจ", form.businessType)}

  <div style="margin-bottom:8px;">
    ${inlineField("โทรศัพท์ :", form.tel, "120px")}
    ${inlineField("เบอร์ต่อ :", form.ext, "80px")}
    ${inlineField("มือถือ :", form.mobile, "120px")}
  </div>

  ${fullLine("ช่องทางการติดต่ออื่นๆ", form.otherContact)}

  <!-- เอกสาร นิติบุคคล -->
  ${sectionTitle("เอกสารประกอบการพิจารณาเบื้องต้น ดังนี้ (นิติบุคคล)")}
  ${docItem(companyFiles.length > 0, "หนังสือรับรองบริษัท มีอายุไม่เกิน 6 เดือน")}
  ${docItem(false, "สำเนา ภ.พ.20 (ในกรณีที่จดทะเบียนภาษีมูลค่าเพิ่ม)")}
  ${docItem(false, "สำเนาสมุดบัญชีธนาคารหรือรูปแบบการชำระเงินหรือโอนเงินของธนาคารรูปแบบหนึ่ง เช่น QR code เป็นต้น")}

  <!-- เอกสาร บุคคลธรรมดา -->
  ${sectionTitle("เอกสารประกอบการพิจารณาเบื้องต้น ดังนี้ (บุคคลธรรมดา)")}
  ${docItem(false, "สำเนาบัตรประชาชน")}
  ${docItem(false, "สำเนาสมุดบัญชีธนาคารหรือรูปแบบการชำระเงินหรือโอนเงินของธนาคารรูปแบบหนึ่ง เช่น QR code เป็นต้น")}

  <!-- เอกสาร สมาคม/มูลนิธิ -->
  ${sectionTitle("เอกสารประกอบการพิจารณาเบื้องต้น ดังนี้ สมาคม/มูลนิธิ/วิสาหกิจชุมชน/องค์การมหาชน/สถานศึกษาและอื่นๆ")}
  ${docItem(false, "สำเนาบัตรประชาชน")}
  ${docItem(agreementFiles.length > 0,
    `เอกสารที่เกี่ยวข้องกับกิจการ (โปรดระบุ)
     <span style="border-bottom:1px solid #000;display:inline-block;min-width:200px;"></span>`)}
  ${docItem(false, "สำเนาสมุดบัญชีธนาคารหรือรูปแบบการชำระเงินหรือโอนเงินของธนาคารรูปแบบหนึ่ง เช่น QR Code เป็นต้น")}

  <!-- เงื่อนไขการชำระเงิน -->
  <div style="margin-top:14px;margin-bottom:6px;">
    <b>เงื่อนไขการชำระเงิน</b>&nbsp;&nbsp;
    ${cb(isTransfer)} โอนเงิน &nbsp;&nbsp;
    ${cb(isCheque)}   รับเช็ค
  </div>
  <div style="margin-bottom:10px;">
    <b>เงื่อนไขเครดิตเทอม</b>&nbsp;&nbsp;
    ${cb(isNoCredit)}  ไม่มีเครดิต &nbsp;&nbsp;
    ${cb(isCredit7)}   เครดิต 7 วัน &nbsp;&nbsp;
    ${cb(isCredit15)}  เครดิต 15 วัน &nbsp;&nbsp;
    ${cb(isCredit30)}  เครดิต 30 วัน
  </div>

  <!-- หมายเหตุ -->
  <div style="margin-bottom:16px;">
    <b>หมายเหตุ</b>
    <span style="display:inline-block;width:calc(100% - 70px);border-bottom:1px solid #000;
      padding-left:4px;vertical-align:bottom;">${form.remark ?? ""}</span>
  </div>

  <!-- Signature block -->
  <table style="width:100%;margin-top:24px;">
    <tr>
      <td style="width:50%;padding-right:20px;vertical-align:top;">
        <div>ลงชื่อ .................................................. ผู้จัดทำ</div>
        <div style="margin-top:8px;">( ........................................................ )</div>
        <div style="margin-top:8px;">ตำแหน่ง .................................................</div>
      </td>
      <td style="width:50%;padding-left:20px;vertical-align:top;">
        <div>ลงชื่อ .................................................. ผู้ตรวจสอบ</div>
        <div style="margin-top:8px;">( ........................................................ )</div>
        <div style="margin-top:8px;">ตำแหน่ง .................................................</div>
      </td>
    </tr>
  </table>

</div>`;

  return page;
}

// ── Export function ──────────────────────────────────────────────────────
export async function exportVendorPDF(form, files) {
   const logoBase64 = await toBase64(process.env.PUBLIC_URL + "/sino-logo.png");
  const html     = buildVendorHTML(form, files);
  const pdf      = await renderToPDF(html);
  const code     = form.vendorCode || "new";
  const filename = `vendor_register_${code}_${new Date().toISOString().split("T")[0]}.pdf`;
  pdf.save(filename);
}

// ── React Button component ───────────────────────────────────────────────
export function ExportVendorPDFButton({ form, files }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      await exportVendorPDF(form, files);
    } finally {
      setLoading(false);
    }
  };
// ── แปลง image path → base64 ────────────────────────────────────────────
async function toBase64(url) {
  const res    = await fetch(url);
  const blob   = await res.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}
  return (
    <Button
      variant="outlined"
      color="error"
      startIcon={loading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
      onClick={handleClick}
      disabled={loading}
    >
      {loading ? "กำลังสร้าง PDF..." : "Export PDF"}
    </Button>
  );
}
